import React from "react";
import { useParams } from "react-router-dom";
import { useProductsStore } from "../../store/productsStore";
import styles from "./ProductPage.module.scss";
import button1 from "../../assets/button1.svg";
import button2 from "../../assets/button2.svg";
import weHave from "../../assets/we-have.svg";
import delivery from "../../assets/delivery.svg";
import proxyUrl from "../../utils/proxyUrl";

export function getDriveImageUrls(raw) {
  if (!raw) return [];
  return [raw.split(",")[0].trim()];
}

function SmartImage({ urls = [], alt, className, style, ...props }) {
  const [i, setI] = React.useState(0);
  const [useProxy, setUseProxy] = React.useState(false);
  const rawSrc = urls[i] || '';
  const src = !rawSrc ? "/placeholder.jpg" : useProxy ? proxyUrl(rawSrc) : rawSrc;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => {
        if (!useProxy && rawSrc && !rawSrc.startsWith('/')) {
          setUseProxy(true);
        } else if (i < urls.length - 1) {
          setUseProxy(false);
          setI(i + 1);
        }
      }}
      {...props}
    />
  );
}

function ProductPage() {
  const { slug } = useParams();

  const {
    products,
    cart,
    addToCart,
    removeFromCart,
    fetch: fetchProducts,
    loading,
    error,
  } = useProductsStore();

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [startIndex, setStartIndex] = React.useState(0);
  const maxVisible = 3;

  React.useEffect(() => {
    if (!products.length && !loading && !error) {
      fetchProducts();
    }
  }, [products.length, loading, error, fetchProducts]);

  const isLoading = loading || (!products.length && !error);

  const decodedSlug = slug ? decodeURIComponent(slug) : "";

  const product =
    products.find(
      (p) =>
        p["slug"] === slug || p["Товар"] === decodedSlug || p["Товар"] === slug
    ) || null;

  const inStock = product
    ? (product["Наличие"] || "").toString().toLowerCase() === "да"
    : false;

  const photoFields = product
    ? [
        product["Фото"],
        product["ДопФото"],
        product["ДопФото2"],
        product["ДопФото3"],
        product["ДопФото4"],
      ].filter(Boolean)
    : [];

  const photosCandidates = photoFields.map(getDriveImageUrls);

  React.useEffect(() => {
    if (!photosCandidates.length) return;
    if (activeIndex < startIndex) {
      setStartIndex(activeIndex);
    } else if (activeIndex >= startIndex + maxVisible) {
      setStartIndex(activeIndex - maxVisible + 1);
    }
  }, [activeIndex, startIndex, photosCandidates.length]);

  const prevPhoto = () => {
    setActiveIndex((idx) =>
      idx === 0 ? photosCandidates.length - 1 : idx - 1
    );
  };

  const nextPhoto = () => {
    setActiveIndex((idx) =>
      idx === photosCandidates.length - 1 ? 0 : idx + 1
    );
  };

  const visibleThumbnails = photosCandidates.slice(
    startIndex,
    startIndex + maxVisible
  );

  const cartItem = product ? cart[product["Товар"]] : null;

  if (isLoading) {
    return (
      <div className={styles.productPage}>
        <h1 className={styles.title}>Загрузка товара...</h1>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.productPage}>
        <h1 className={styles.title}>Ошибка загрузки</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.productPage}>
        <h1 className={styles.title}>Товар не найден</h1>
      </div>
    );
  }

  return (
    <div className={styles.productPage}>
      <h1 className={styles.title}>{product["Товар"]}</h1>

      <div className={styles.wrap}>
        <div className={styles.wrappImages}>
          <div className={styles.additionalPhotos}>
            {visibleThumbnails.map((urls, idx) => {
              const realIndex = startIndex + idx;
              return (
                <SmartImage
                  key={realIndex}
                  urls={urls}
                  alt={`Фото ${realIndex + 1}`}
                  className={`${styles.thumb} ${
                    realIndex === activeIndex ? styles.activeThumb : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveIndex(realIndex)}
                />
              );
            })}
            <div className={styles.buttons}>
              <button onClick={prevPhoto} className={styles.navButton}>
                <img src={button1} alt="Назад" />
              </button>
              <button onClick={nextPhoto} className={styles.navButton}>
                <img src={button2} alt="Вперёд" />
              </button>
            </div>
          </div>

          <div className={styles.mainPhoto}>
            {photosCandidates.length ? (
              <SmartImage
                urls={photosCandidates[activeIndex]}
                alt={product["Товар"]}
                className={styles.mainImg}
              />
            ) : (
              <img
                src="/placeholder.jpg"
                alt="Нет фото"
                className={styles.mainImg}
              />
            )}
          </div>
        </div>

        <div className={styles.priceAndBuy}>
          <p className={styles.price}>
            <b>{product["Цена"]} ₽</b>
          </p>

          {!cartItem ? (
            <button
              className={`${styles.buyButton} ${
                !inStock ? styles.disabledButton : ""
              }`}
              onClick={() => inStock && addToCart(product)}
              disabled={!inStock}
            >
              В корзину
            </button>
          ) : (
            <div className={styles.counter}>
              <button onClick={() => removeFromCart(product)}>-</button>
              <span>{cartItem.quantity}</span>
              <button onClick={() => addToCart(product)}>+</button>
            </div>
          )}

          <p className={styles.materials}>{product["Материалы"]}</p>
          <p className={styles.stock}>
            <img src={weHave} alt="we have it" />
            {inStock ? "В наличии" : "Нет в наличии"} {product["Количество"]}
          </p>
        </div>
      </div>

      <section className={styles.descriptionSection}>
        <h2>Описание</h2>
        <p>{product["Описание"]}</p>

        <h2>Рекомендации по уходу</h2>
        <p>{product["Уход"]}</p>

        <h2>Доставка и оплата</h2>
        <div className={styles.delivery}>
          <img src={delivery} alt="" />
          <p>Срок отправки: {product["Доставка"]}</p>
        </div>
      </section>
    </div>
  );
}

export default ProductPage;
